import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";

import { useSelector } from "react-redux";
import SpecialBusinessHoursDeleteDialog from "../modals/SpecialBusinessHoursDeleteDialog";
import {
  getSpecialBusinessHours,
  createSpecialBusinessHours,
  updateSpecialBusinessHours,
  deleteSpecialBusinessHours,
} from "../api/SpecialBusinessHoursApi";
import SpecialBusinessHoursDialog from "../modals/SpecialBusinessHoursDialog";

const SpecialBusinessHours = () => {
  const { token, lang, location } = useSelector((state) => state.user);

  const isFrench = lang === "FR";

  const noEmplacement = location?.noEmplacement;

  // ============================================================
  // LOCATION LABEL
  // ============================================================

  const locationLabel = useMemo(() => {
    if (!location) return "";

    const name = location.nom || location.nomEmplacement || location.name || "";

    const accpac = location.noAccpac || location.referenceCode || "";

    if (name && accpac) {
      return `${name} | ${accpac}`;
    }

    if (name) {
      return name;
    }

    if (accpac) {
      return accpac;
    }

    return noEmplacement ? String(noEmplacement) : "";
  }, [location, noEmplacement]);

  const [specialHours, setSpecialHours] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ============================================================
  // Load Special Business Hours
  // ============================================================

  const loadSpecialHours = async () => {
    if (!noEmplacement) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await getSpecialBusinessHours(token, noEmplacement);

      if (response?.success === false) {
        setError(isFrench ? response.messageFr : response.messageEn);

        setSpecialHours([]);
        return;
      }

      setSpecialHours(response?.data || []);
    } catch (err) {
      console.error("Error loading special business hours:", err);

      setError(
        isFrench
          ? err?.messageFr ||
              "Une erreur est survenue lors du chargement des heures d'ouverture spéciales."
          : err?.messageEn ||
              "An error occurred while loading special business hours.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSpecialHours();
  }, [noEmplacement]);

  // ============================================================
  // Helpers
  // ============================================================

  const formatDate = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString(isFrench ? "fr-CA" : "en-CA", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getMonthDay = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return parsedDate.toLocaleDateString(isFrench ? "fr-CA" : "en-CA", {
      month: "short",
      day: "numeric",
    });
  };

  // ============================================================
  // Detect a full-day closure
  // Supports explicit API values and the UI's 00:00 -> 12:00
  // convention (including values containing seconds).
  // ============================================================

  const isClosedAllDay = (item) => {
    if (!item) return false;

    const normalizeState = (value) =>
      String(value ?? "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

    const state = normalizeState(
      item.state ?? item.State ?? item.status ?? item.Status,
    );

    if (
      ["closed", "close", "ferme", "fermee", "closedallday"].includes(state)
    ) {
      return true;
    }

    if (
      item.isClosed === true ||
      item.closedAllDay === true ||
      item.isClosedAllDay === true
    ) {
      return true;
    }

    const normalizeTime = (value) => {
      let time = String(value ?? "")
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "");

      // Remove seconds from database time values, e.g. 00:00:00 -> 00:00.
      time = time.replace(/^(\d{1,2}:\d{2}):\d{2}$/, "$1");

      // Normalize 12-hour forms into comparable values.
      if (time === "12:00AM") return "00:00";
      if (time === "12:00PM") return "12:00";

      return time;
    };

    const opening = normalizeTime(item.opening ?? item.Opening);
    const closing = normalizeTime(item.closing ?? item.Closing);

    return ["00:00", "0:00"].includes(opening) && ["12:00"].includes(closing);
  };

  // ============================================================
  // Actions - CRUD
  // ============================================================

  const handleAdd = () => {
    setSelectedItem(null);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setDialogOpen(true);
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) {
      return;
    }

    setDeleteDialogOpen(false);
    setDeleteItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) {
      return;
    }

    try {
      setDeleteLoading(true);
      setError("");

      const date = deleteItem.date.split("T")[0];

      const response = await deleteSpecialBusinessHours(
        token,
        noEmplacement,
        date,
      );

      if (response?.success === false) {
        setError(isFrench ? response.messageFr : response.messageEn);

        return;
      }

      await loadSpecialHours();

      setDeleteDialogOpen(false);
      setDeleteItem(null);
    } catch (err) {
      console.error("Error deleting special business hours:", err);

      setError(
        isFrench
          ? err?.messageFr || "Une erreur est survenue lors de la suppression."
          : err?.messageEn ||
              "An error occurred while deleting the special business hours.",
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (dialogLoading) {
      return;
    }

    setDialogOpen(false);
    setSelectedItem(null);
  };

  const handleDialogSave = async (payload) => {
    try {
      setDialogLoading(true);
      setError("");

      // ========================================================
      // CREATE
      // ========================================================

      if (!selectedItem) {
        const response = await createSpecialBusinessHours(
          token,
          noEmplacement,
          payload,
        );

        if (response?.success === false) {
          setError(isFrench ? response.messageFr : response.messageEn);

          return;
        }

        await loadSpecialHours();

        setDialogOpen(false);
        setSelectedItem(null);

        return;
      }

      // ========================================================
      // UPDATE
      // ========================================================

      const originalDate = selectedItem.date.split("T")[0];

      const response = await updateSpecialBusinessHours(
        token,
        noEmplacement,
        originalDate,
        payload,
      );

      if (response?.success === false) {
        setError(isFrench ? response.messageFr : response.messageEn);

        return;
      }

      await loadSpecialHours();

      setDialogOpen(false);
      setSelectedItem(null);
    } catch (err) {
      console.error("Error saving special business hours:", err);

      setError(
        isFrench
          ? err?.messageFr ||
              "Une erreur est survenue lors de l'enregistrement des heures d'ouverture spéciales."
          : err?.messageEn ||
              "An error occurred while saving the special business hours.",
      );
    } finally {
      setDialogLoading(false);
    }
  };

  // ============================================================
  // Loading
  // ============================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={36} />

          <Typography variant="body2" color="text.secondary">
            {isFrench
              ? "Chargement des heures spéciales..."
              : "Loading special business hours..."}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // ============================================================
  // Page
  // ============================================================

  return (
    <Box
      sx={{
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
        py: {
          xs: 2,
          md: 4,
        },
        maxWidth: 1500,
        mx: "auto",
      }}
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          justifyContent: "space-between",
          gap: 3,
          mb: 4,
          flexDirection: {
            xs: "column",
            md: "row",
          },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              color: "#fff",
              boxShadow: "0 8px 20px rgba(25, 118, 210, 0.25)",
            }}
          >
            <ScheduleRoundedIcon sx={{ fontSize: 30 }} />
          </Box>

          <Box>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                fontSize: {
                  xs: "1.65rem",
                  md: "2rem",
                },
              }}
            >
              {isFrench
                ? "Heures d'ouvertures spéciales"
                : "Special Business Hours"}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {isFrench
                ? "Gérez les horaires exceptionnels de votre emplacement."
                : "Manage exceptional opening hours for your location."}
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          size="large"
          startIcon={<AddRoundedIcon />}
          onClick={handleAdd}
          sx={{
            borderRadius: 2,
            px: 2.5,
            py: 1.3,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "0 6px 16px rgba(25, 118, 210, 0.25)",
            whiteSpace: "nowrap",
          }}
        >
          {isFrench ? "Ajouter une heure spéciale" : "Add Special Hours"}
        </Button>
      </Box>

      {/* ======================================================
          ERROR
      ====================================================== */}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* ======================================================
          LOCATION CARD
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2,
                backgroundColor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StorefrontRoundedIcon color="primary" />
            </Box>

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.7,
                }}
              >
                {isFrench ? "Emplacement" : "Location"}
              </Typography>

              {/* ==================================================
                  LOCATION NAME + NO EMPLACEMENT
              ================================================== */}

              <Typography variant="h6" fontWeight={700}>
                {locationLabel}
              </Typography>

              {/* Optionnel : afficher explicitement le numéro */}
              {locationLabel !== String(noEmplacement) && noEmplacement && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.25 }}
                >
                  {isFrench
                    ? `No emplacement : ${noEmplacement}`
                    : `Location number: ${noEmplacement}`}
                </Typography>
              )}
            </Box>
          </Stack>

          <Chip
            icon={<EventAvailableRoundedIcon />}
            label={
              isFrench
                ? `${specialHours.length} horaire${
                    specialHours.length > 1 ? "s" : ""
                  } configuré${specialHours.length > 1 ? "s" : ""}`
                : `${specialHours.length} configured ${
                    specialHours.length === 1 ? "special hour" : "special hours"
                  }`
            }
            color="primary"
            variant="outlined"
            sx={{
              fontWeight: 600,
            }}
          />
        </Box>
      </Paper>

      {/* ======================================================
          MAIN TABLE CARD
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {isFrench ? "Horaires configurés" : "Configured Hours"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {isFrench
                  ? "Seules les dates courantes et futures sont affichées."
                  : "Only current and future dates are displayed."}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider />

        {specialHours.length === 0 ? (
          <Box
            sx={{
              py: 9,
              px: 3,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 76,
                height: 76,
                mx: "auto",
                mb: 2.5,
                borderRadius: "50%",
                backgroundColor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarMonthRoundedIcon
                sx={{
                  fontSize: 38,
                  color: "text.secondary",
                }}
              />
            </Box>

            <Typography variant="h6" fontWeight={700}>
              {isFrench ? "Aucun horaire spécial" : "No special hours"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 1,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              {isFrench
                ? "Aucune heure d'ouverture spéciale n'est actuellement configurée pour cet emplacement."
                : "There are currently no special business hours configured for this location."}
            </Typography>

            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={handleAdd}
              sx={{
                mt: 3,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {isFrench
                ? "Ajouter le premier horaire"
                : "Add the first special hour"}
            </Button>
          </Box>
        ) : (
          <TableContainer
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                // Fixed layout prevents a long "Closed all day" chip
                // from resizing columns and shifting the Actions column.
                tableLayout: "fixed",
                minWidth: 1080,
                width: "100%",
              }}
            >
              <colgroup>
                <col style={{ width: "26%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "30%" }} />
                <col style={{ width: "18%" }} />
              </colgroup>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "action.hover",
                  }}
                >
                  <TableCell
                    sx={{ fontWeight: 700, py: 2, whiteSpace: "nowrap" }}
                  >
                    Date
                  </TableCell>

                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {isFrench ? "Ouverture" : "Opening"}
                  </TableCell>

                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {isFrench ? "Fermeture" : "Closing"}
                  </TableCell>

                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {isFrench ? "Type d'horaire" : "Schedule Type"}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                  >
                    {isFrench ? "Actions" : "Actions"}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {specialHours.map((item) => {
                  const closedAllDay = isClosedAllDay(item);

                  return (
                    <TableRow
                      key={`${item.noEmplacement}-${item.date}`}
                      hover
                      sx={{
                        "&:last-child td": {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: 1.5,
                              backgroundColor: "primary.lighter",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px solid",
                              borderColor: "primary.100",
                            }}
                          >
                            <CalendarMonthRoundedIcon
                              fontSize="small"
                              color="primary"
                            />
                          </Box>

                          <Box>
                            <Typography fontWeight={600}>
                              {getMonthDay(item.date)}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDate(item.date)}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {closedAllDay ? (
                          <Typography color="text.secondary">—</Typography>
                        ) : (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <AccessTimeRoundedIcon
                              fontSize="small"
                              color="success"
                            />

                            <Typography fontWeight={600}>
                              {item.opening || "—"}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>

                      <TableCell>
                        {closedAllDay ? (
                          <Typography color="text.secondary">—</Typography>
                        ) : (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <AccessTimeRoundedIcon
                              fontSize="small"
                              color="error"
                            />

                            <Typography fontWeight={600}>
                              {item.closing || "—"}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>

                      <TableCell sx={{ overflow: "hidden" }}>
                        {closedAllDay ? (
                          <Chip
                            icon={<EventAvailableRoundedIcon />}
                            label={
                              isFrench
                                ? "Toute la journée est fermée"
                                : "Closed all day"
                            }
                            color="error"
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              maxWidth: "100%",
                              height: "auto",
                              "& .MuiChip-label": {
                                display: "block",
                                whiteSpace: "normal",
                                py: 0.5,
                              },
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<AccessTimeRoundedIcon />}
                            label={
                              isFrench
                                ? "Heures personnalisées"
                                : "Custom hours"
                            }
                            color="primary"
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              maxWidth: "100%",
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          whiteSpace: "nowrap",
                          px: 1.5,
                        }}
                      >
                        <Tooltip title={isFrench ? "Modifier" : "Edit"}>
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(item)}
                            sx={{
                              mr: 0.5,
                            }}
                          >
                            <EditRoundedIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={isFrench ? "Supprimer" : "Delete"}>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(item)}
                          >
                            <DeleteOutlineRoundedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <SpecialBusinessHoursDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        item={selectedItem}
        loading={dialogLoading}
        lang={lang}
      />

      <SpecialBusinessHoursDeleteDialog
        open={deleteDialogOpen}
        item={deleteItem}
        loading={deleteLoading}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        lang={lang}
      />
    </Box>
  );
};

export default SpecialBusinessHours;
